import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ManagerSelector } from './ManagerSelector';
import { ExaminerSelector } from './ExaminerSelector';
import { OrganizationForm } from './OrganizationForm';
import { OrganizationMembers } from './OrganizationMembers';
import { OrganizationTeachers } from './OrganizationTeachers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Organization } from '@/types/organization';

interface ManageOrganizationDialogProps {
  organization: Organization | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ManageOrganizationDialog({
  organization,
  open,
  onOpenChange,
  onSuccess
}: ManageOrganizationDialogProps) {
  const [activeTab, setActiveTab] = useState('details');
  const { t, dir } = useLanguage();

  // Reset tab when dialog opens/closes
  useEffect(() => {
    if (open) {
      setActiveTab('details');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] w-[95vw] overflow-hidden" dir={dir}>
        <DialogHeader className={`pb-4 ${dir === 'rtl' ? '!text-right' : '!text-left'}`}>
          <DialogTitle className="text-2xl">{t('dashboard.admin.organizations.manage.title')}</DialogTitle>
          <DialogDescription className="text-base">
            {t('dashboard.admin.organizations.manage.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <TabsList className={`flex w-full mb-6 ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'}`}>
              <TabsTrigger value="details" className="text-sm font-medium flex-1">{t('dashboard.admin.organizations.tabs.details')}</TabsTrigger>
              <TabsTrigger value="managers" className="text-sm font-medium flex-1">{t('dashboard.admin.organizations.tabs.managers')}</TabsTrigger>
              <TabsTrigger value="examiners" className="text-sm font-medium flex-1">{t('dashboard.admin.organizations.tabs.examiners')}</TabsTrigger>
              <TabsTrigger value="customers" className="text-sm font-medium flex-1">{t('dashboard.admin.organizations.tabs.customers')}</TabsTrigger>
              <TabsTrigger value="instructors" className="text-sm font-medium flex-1">{t('dashboard.admin.organizations.tabs.instructors')}</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-hidden">
              <TabsContent value="details" className="h-full overflow-y-auto data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-left-2">
                <OrganizationForm 
                  organization={organization}
                  onSuccess={onSuccess}
                  onClose={() => onOpenChange(false)}
                />
              </TabsContent>

              <TabsContent value="managers" className="h-full overflow-y-auto data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-left-2">
                <ManagerSelector 
                  organization={organization}
                  onSuccess={onSuccess}
                />
              </TabsContent>

              <TabsContent value="examiners" className="h-full overflow-y-auto data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-left-2">
                <ExaminerSelector 
                  organization={organization}
                  onSuccess={onSuccess}
                />
              </TabsContent>

              <TabsContent value="customers" className="h-full overflow-y-auto data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-left-2">
                <OrganizationMembers 
                  organization={organization}
                  onSuccess={onSuccess}
                />
              </TabsContent>

              <TabsContent value="instructors" className="h-full overflow-y-auto data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-left-2">
                <OrganizationTeachers 
                  organization={organization}
                  onSuccess={onSuccess}
                />
              </TabsContent>

            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}